/**
 * The Gantt tab: the project's phases and tasks on a time axis.
 *
 * The roadmap tab next door groups the same rows into four columns by how far
 * off they are. That answers "what is coming up" and cannot answer "does this
 * plan fit" - a board has no scale, so two tasks in the same column may be a day
 * or a quarter apart. This tab is the other question, and it is the one an
 * operator asks with a client on the phone.
 *
 * ── ONE REQUEST, THE BOARD'S OWN ──────────────────────────────────────────
 *
 * `getMilestoneBoard` already answers with the phases AND their tasks nested
 * inside them, in the order the board draws them, with an always-present unfiled
 * column. That is exactly the shape a Gantt needs, so this tab adds no endpoint:
 * a second one would be a second definition of "which tasks belong to this
 * phase", and the two would disagree the first time either changed.
 *
 * It also brings `truncated`, which matters more here than on the board. A board
 * that shows the first page of a huge column still looks like a board; a
 * timeline missing rows looks like a project that finishes earlier than it does.
 *
 * ── WHAT THE CHART DELIBERATELY DOES NOT DRAW ─────────────────────────────
 *
 * Rows with no dates at all. They are counted and reported in a line under the
 * chart instead. Drawing them as bars would mean inventing dates; drawing them
 * as empty rows would push the dated work off the screen on exactly the projects
 * that need reading - most tasks in this CRM carry a due date and nothing else,
 * and a fair number carry neither. The count is the honest version: the chart
 * says what it is showing and what it is not.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarRange, Loader2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { getMilestoneBoard, type MilestoneBoardColumn } from "../../../../lib/milestoneBoard";
import type { Milestone, Project } from "../../../../lib/projects";
import { Card, EmptyState, PillTabs } from "../../../ui";
import { GanttChart, type GanttWords } from "./GanttChart";
import { GANTT_SCALES, buildGanttModel, ganttWindow, type GanttColumn, type GanttScale } from "./ganttModel";

/** A `Record`, so a scale added to `GANTT_SCALES` without a word is a type error
 *  rather than a pill with a raw key printed on it. */
const SCALE_LABEL: Record<GanttScale, string> = {
  day: "projects.gantt.scaleDay",
  week: "projects.gantt.scaleWeek",
  month: "projects.gantt.scaleMonth",
};

export function ProjectGantt({ project }: { project: Project & { milestones: Milestone[] } }) {
  const t = useT();
  const [columns, setColumns] = useState<MilestoneBoardColumn[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState<GanttScale>("week");

  /**
   * The clock, taken ONCE per mount and held in state.
   *
   * Not `Date.now()` at each use. The today line, the window and every "late"
   * decision have to be made against the same instant, or a tab left open across
   * midnight draws a line that contradicts the colours beside it. It also keeps
   * the memos below from recomputing on every render.
   */
  const [nowMs] = useState(() => Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const board = await getMilestoneBoard(project.id);
      setColumns(board.columns);
      setTruncated(board.truncated);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const model = useMemo(() => {
    const cols: GanttColumn[] = (columns ?? []).map((c) => ({ milestone: c.milestone, tasks: c.tasks }));
    return buildGanttModel(project, cols, nowMs, t("projects.noMilestone"));
  }, [columns, project, nowMs, t]);

  const win = useMemo(() => ganttWindow(model.rows, nowMs, scale), [model.rows, nowMs, scale]);

  const words = useMemo<GanttWords>(
    () => ({
      start: t("projects.gantt.start"),
      due: t("projects.gantt.due"),
      noStart: t("projects.gantt.noStart"),
      noDue: t("projects.gantt.noDue"),
      derived: t("projects.gantt.derived"),
      today: t("projects.gantt.today"),
      chartLabel: t("projects.gantt.chartLabel", { name: project.name }),
    }),
    [t, project.name],
  );

  if (loading) {
    return (
      <Card className="flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </Card>
    );
  }

  if (failed) {
    return (
      <Card className="px-6 py-5">
        <EmptyState icon={CalendarRange} title={t("common.error")} body={t("projects.gantt.loadFailed")} />
      </Card>
    );
  }

  // Only the project row survived, and it has no dates either. There is nothing
  // to put on a timeline, and an axis over an empty canvas reads as a chart that
  // failed to load rather than as a project nobody has scheduled.
  const nothingDated = model.rows.every((r) => !r.span);

  return (
    <Card className="crm-gantt-card">
      <div className="crm-gantt-head">
        <div className="min-w-0">
          <h3 className="crm-card-t">{t("projects.gantt.title")}</h3>
          <p className="crm-card-s">{t("projects.gantt.intro")}</p>
        </div>
        <PillTabs
          small
          value={scale}
          onChange={setScale}
          tabs={GANTT_SCALES.map((s) => ({ value: s, label: t(SCALE_LABEL[s]) }))}
        />
      </div>

      {nothingDated ? (
        <div className="px-6 pb-5">
          <EmptyState
            icon={CalendarRange}
            title={t("projects.gantt.emptyTitle")}
            body={t("projects.gantt.emptyBody")}
          />
        </div>
      ) : (
        <GanttChart rows={model.rows} win={win} scale={scale} nowMs={nowMs} words={words} />
      )}

      {/* What the chart is NOT showing, always under it and never as a tooltip.
          An operator reading a timeline is deciding whether a date is reachable,
          and "twelve of these tasks have no date at all" changes that answer. */}
      <div className="crm-gantt-foot">
        <span className="crm-gantt-key">
          <i className="crm-gantt-swatch is-phase" aria-hidden />
          {t("projects.gantt.keyPhase")}
          <i className="crm-gantt-swatch is-task" aria-hidden />
          {t("projects.gantt.keyTask")}
          <i className="crm-gantt-swatch is-late" aria-hidden />
          {t("projects.gantt.keyLate")}
          <i className="crm-gantt-swatch is-done" aria-hidden />
          {t("projects.gantt.keyDone")}
          <i className="crm-gantt-swatch is-mark" aria-hidden />
          {t("projects.gantt.keyMarker")}
        </span>
        {model.undatedTasks > 0 ? (
          <span className="crm-mut">{t("projects.gantt.undatedTasks", { n: model.undatedTasks })}</span>
        ) : null}
        {model.undatedMilestones > 0 ? (
          <span className="crm-mut">{t("projects.gantt.undatedMilestones", { n: model.undatedMilestones })}</span>
        ) : null}
        {/* The board's cap. Said out loud because a timeline that is missing rows
            reads as a project that finishes earlier than it does - the one lie a
            chart like this can tell without looking wrong. */}
        {truncated ? <span className="text-red-600">{t("projects.gantt.truncated")}</span> : null}
      </div>
    </Card>
  );
}
